import React, { useEffect, useRef } from 'react';
import { TextField, InputAdornment, CircularProgress } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useFormContext, useWatch, useFormState } from 'react-hook-form';
import { useCheckSlugUniqueness } from '../../../../hooks/useCheckSlugUniqueness';
import { SiteImportFormData } from '../../../../schemas/siteImportSchema';

const SiteNameCodeFields: React.FC = () => {
  const form = useFormContext<SiteImportFormData>();
  const { control, setValue } = form;
  const { errors } = useFormState({ control });

  const name = useWatch({ control, name: 'name' }) ?? '';
  const slug = useWatch({ control, name: 'slug' }) ?? '';

  const { checkSlug, isChecking: slugChecking } = useCheckSlugUniqueness(form);
  const slugManuallyEdited = useRef(false);

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setValue('name', value, { shouldValidate: true, shouldDirty: true });
    if (!slugManuallyEdited.current) {
      const derivedSlug = value
        .replace(/[^a-zA-Z0-9]/g, '')
        .toUpperCase()
        .slice(0, 4);
      setValue('slug', derivedSlug, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  const handleSlugChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 4);
    slugManuallyEdited.current = value.length > 0;
    setValue('slug', value, { shouldValidate: true, shouldDirty: true });
  };

  useEffect(() => {
    if (slug.length === 4) {
      checkSlug(slug);
    }
  }, [slug, checkSlug]);

  const getSlugAdornment = () => {
    if (slug.length !== 4) return null;
    if (slugChecking) return <CircularProgress size={20} />;
    if (errors.slug) return <ErrorOutlineIcon color="error" />;
    if (!slugChecking) return <CheckCircleOutlineIcon color="success" />;
    return null;
  };

  const getSlugHelperText = () => {
    if (slug.length === 0) {
      return 'Required - 4 uppercase letters/numbers (e.g., Brough could be BRGH)';
    }
    if (errors.slug) return errors.slug.message;
    if (slugChecking) return 'Checking availability...';
    if (slug.length === 4 && !slugChecking && !errors.slug)
      return 'This slug is available';
    return '4 uppercase letters/numbers (e.g., Brough could be BRGH)';
  };

  return (
    <>
      <TextField
        label="Site Name"
        value={name}
        onChange={handleNameChange}
        required
        fullWidth
        error={Boolean(errors.name)}
        helperText={
          errors.name?.message ?? 'Must be at least 3 characters long'
        }
        inputProps={{ 'data-testid': 'site-name-input' }}
      />
      <TextField
        label="Site Code"
        value={slug}
        onChange={handleSlugChange}
        required
        fullWidth
        placeholder="X7K2"
        helperText={getSlugHelperText()}
        error={Boolean(errors.slug)}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                {getSlugAdornment()}
              </InputAdornment>
            ),
          },
        }}
        inputProps={{
          'data-testid': 'site-slug-input',
          maxLength: 4,
          style: { textTransform: 'uppercase' },
        }}
      />
    </>
  );
};

export default SiteNameCodeFields;
